import { useEffect, useState } from 'react';
import { warrantiesAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { toast } from 'sonner';
import { MapPin, Globe, Package, Loader2 } from 'lucide-react';

export const WarrantyRegionsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await warrantiesAPI.getRegions();
        setData(res.data);
      } catch (e) {
        toast.error('Failed to load region data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  }

  const maxProvince = Math.max(1, ...(data?.by_province || []).map(p => p.count));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="warranty-regions-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] heading-tight flex items-center gap-2">
          <MapPin className="h-7 w-7 text-blue-600" /> Installed Base by Region
        </h1>
        <p className="text-muted-foreground mt-1">Where your registered freeze dryers are located ({data?.total || 0} registrations).</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Globe className="h-5 w-5 text-blue-600" /> By Country</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(data?.by_country || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No registrations yet.</p>
            ) : data.by_country.map(c => (
              <div key={c.region} className="flex items-center justify-between">
                <span>{c.region}</span>
                <Badge variant="outline">{c.count}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><MapPin className="h-5 w-5 text-blue-600" /> By Province / State</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {(data?.by_province || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No registrations yet.</p>
            ) : data.by_province.map(p => (
              <div key={p.region}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span>{p.region}</span>
                  <span className="text-muted-foreground">{p.count}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded">
                  <div className="h-2 bg-blue-500 rounded" style={{ width: `${(p.count / maxProvince) * 100}%` }} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Package className="h-5 w-5 text-emerald-600" /> Registrations</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Serial</TableHead>
                <TableHead>City</TableHead>
                <TableHead>Province/State</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.registrations || []).length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No registrations yet</TableCell></TableRow>
              ) : data.registrations.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.product_name || '—'}</TableCell>
                  <TableCell><code className="text-xs bg-slate-100 px-1 rounded">{r.serial_number || '—'}</code></TableCell>
                  <TableCell>{r.city || '—'}</TableCell>
                  <TableCell>{r.province_state || '—'}</TableCell>
                  <TableCell>{r.country || '—'}</TableCell>
                  <TableCell><Badge variant="outline">{r.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default WarrantyRegionsPage;
